import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Sparkles, Copy, CheckCircle2, Search, Crown, ShoppingBag, Download, Loader2, X, Coins, Image as ImageIcon, FileText } from "lucide-react";

// 30 caption / text templates (kept as a free bonus tab for creators).
const CAPTION_TEMPLATES = [
  { id: 1, category: "Hook", title: "POV opener", body: "POV: You just unlocked the secret nobody talks about 👀\n\nStay till the end — it'll change how you {{topic}} forever." },
  { id: 2, category: "Hook", title: "Pattern interrupt", body: "Stop scrolling. ✋\n\nIf you {{problem}}, this 30-second reel is for YOU. Trust me." },
  { id: 3, category: "Hook", title: "Curiosity gap", body: "Nobody told me this when I started {{topic}}…\n\nAnd it cost me 6 months of mistakes. Watch till the end 👇" },
  { id: 4, category: "Hook", title: "Bold claim", body: "I'll do {{goal}} faster than 99% of people, and here's exactly how 👇" },
  { id: 5, category: "Hook", title: "Question hook", body: "Are you still {{old_method}}?\n\nThere's a 10x easier way. Let me show you…" },
  { id: 6, category: "Caption", title: "Storytime caption", body: "1 year ago I had nothing.\nToday I {{achievement}}.\n\nHere's the exact playbook I used 👇\n\n#hifireels #creator #grind" },
  { id: 7, category: "Caption", title: "Listicle caption", body: "5 things I wish I knew about {{topic}}:\n\n1. ✨ {{tip1}}\n2. 🚀 {{tip2}}\n3. 💡 {{tip3}}\n4. 🔥 {{tip4}}\n5. ❤️ {{tip5}}\n\nSave this for later 📌" },
  { id: 8, category: "Caption", title: "Emotional caption", body: "Sometimes the best advice comes from the people you least expect.\n\n{{lesson}}.\n\nDouble tap if this resonates ♥" },
  { id: 9, category: "Caption", title: "Behind-the-scenes", body: "Behind the scenes of {{thing}} 🎬\n\nMost people only see the result. Here's the real grind." },
  { id: 10, category: "Caption", title: "Quick tip caption", body: "Quick tip for {{audience}} 👇\n\n{{tip}}\n\nTry it this week and tag me when you do." },
  { id: 11, category: "CTA", title: "Comment magnet", body: "Drop a 🔥 in the comments if you want Part 2 — I'll post it tomorrow if we hit 100 fires." },
  { id: 12, category: "CTA", title: "Save & share CTA", body: "Save this so you don't forget. Share it with a friend who needs to see this today 💌" },
  { id: 13, category: "CTA", title: "Vote CTA", body: "Which one are you? A or B?\n\nA = {{option_a}}\nB = {{option_b}}\n\nLet me know in the comments 👇" },
  { id: 14, category: "CTA", title: "Follow-loop", body: "Follow @{{handle}} for daily {{niche}} content that actually works. No fluff." },
  { id: 15, category: "CTA", title: "DM trigger", body: "Want the full {{resource}}? Comment \"YES\" and I'll DM it to you 💌" },
  { id: 16, category: "Story", title: "Poll sticker", body: "Quick one for the audience 🙋\n\nQ: {{question}}\nA: {{option_a}}\nB: {{option_b}}\n\n👉 Vote on the sticker" },
  { id: 17, category: "Story", title: "Behind-scenes story", body: "Day {{day}} of building {{project}} ⚒️\n\nToday's win: {{win}}\nToday's lesson: {{lesson}}" },
  { id: 18, category: "Story", title: "Q&A starter", body: "I'm bored. Ask me ANYTHING about {{topic}} 👇\n\nReply to this and I'll answer every single one." },
  { id: 19, category: "Story", title: "Countdown", body: "🚨 {{event}} drops in {{time}} 🚨\n\nSet your countdown reminder. You don't want to miss this one." },
  { id: 20, category: "Brand", title: "Pitch a brand (DM)", body: "Hi {{brand}} team! 👋\n\nI'm {{name}}, a creator on Hifireels with {{followers}} followers in the {{niche}} space.\n\nMy audience is {{audience_desc}}, with average reach of {{avg_views}}/reel.\n\nI'd love to collab on {{idea}} — would you be open to a quick chat? Sample content + rate card attached." },
  { id: 21, category: "Brand", title: "Sponsored post disclosure", body: "#ad — Honestly excited to share this. I've been using {{product}} for {{duration}} and {{result}}.\n\nHere's what stood out: {{point1}}, {{point2}}, {{point3}}.\n\nLink in bio for {{discount}}." },
  { id: 22, category: "Brand", title: "Creator collab DM", body: "Hey @{{creator}} 🙌\n\nLoved your latest reel on {{topic}}!\n\nI think our audiences would mesh well — would you be open to a collab? Could do a duet/remix/joint live. Let me know what works." },
  { id: 23, category: "Brand", title: "UGC pitch", body: "Hi {{brand}}!\n\nI'd love to create UGC for {{product}}. Quick portfolio: {{portfolio_link}}. Average engagement {{engagement}}.\n\nOpen to gifted, paid, or hybrid. Rate sheet on request 💌" },
  { id: 24, category: "Growth", title: "Milestone post", body: "🎉 We just crossed {{milestone}}!\n\nNone of this happens without YOU showing up every day. Tag the friend who got you into {{topic}} — let's celebrate them too." },
  { id: 25, category: "Growth", title: "Re-engage post", body: "Hey {{audience}} 👋\n\nQuick reality check: I've been quiet because I was building {{project}}. It's finally ready.\n\nDouble tap if you want me to share the inside story." },
  { id: 26, category: "Growth", title: "Trend hop", body: "Couldn't resist this trend 😂\n\nUsing {{sound}} to share {{message}}. Tag someone who needs to see this." },
  { id: 27, category: "Hashtags", title: "Hifireels viral pack", body: "#hifireels #reels #viral #trending #explorepage #fyp #foryou #goldly #creator #shortvideo #reelitfeelit #indiancreator #livestream #tokwin #reelsindia" },
  { id: 28, category: "Hashtags", title: "Dance / music pack", body: "#dancereels #danceindia #reeldance #musicreels #danceedit #danceforreels #hifireels #reels #trendingsong #viral #goldly" },
  { id: 29, category: "Hashtags", title: "Comedy pack", body: "#comedyreels #funnyvideo #standupindia #hifireels #lol #memes #reelitfeelit #relatable #desihumor #reels #viral" },
  { id: 30, category: "Hashtags", title: "Lifestyle / vlog pack", body: "#vlogger #dailyvlog #lifestyle #hifireels #routine #grwm #aesthetic #moodboard #behindthescenes #reelsindia #creator" },
];

const IMAGE_CATEGORIES = ["All", "reel-cover", "story", "quote", "festival", "religious", "wedding", "birthday", "workshop", "portfolio", "party", "business", "fashion"];
const CAT_LABELS = { "All": "All", "reel-cover": "Reel Covers", "story": "Stories", "quote": "Quotes", "festival": "Festivals", "religious": "Religious", "wedding": "Wedding", "birthday": "Birthday", "workshop": "Workshop", "portfolio": "Portfolio", "party": "Parties", "business": "Business", "fashion": "Fashion" };

export default function Templates() {
  const { refresh } = useAuth();
  const [tab, setTab] = useState("images"); // images | captions
  const [imageTemplates, setImageTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [previewing, setPreviewing] = useState(null);
  const [buying, setBuying] = useState(false);
  const [buyMsg, setBuyMsg] = useState("");

  // Captions tab state
  const [capCat, setCapCat] = useState("All");
  const [capQ, setCapQ] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const loadImages = () => {
    setLoading(true);
    api.get("/templates")
      .then(r => setImageTemplates(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadImages(); }, []);

  const filtered = imageTemplates.filter(t => {
    if (cat !== "All" && t.category !== cat) return false;
    if (q && !t.name.toLowerCase().includes(q.toLowerCase()) && !(t.description || "").toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const buy = async (t) => {
    setBuying(true); setBuyMsg("");
    try {
      const { data } = await api.post(`/templates/${t.id}/purchase`);
      setBuyMsg(data.already_owned ? "Already in your library" : `Purchased! ${data.new_balance_goldly} Goldly left`);
      await refresh?.();
      loadImages();
    } catch (e) {
      setBuyMsg(e?.response?.data?.detail || "Purchase failed");
    } finally { setBuying(false); }
  };

  const download = (t) => {
    const url = t.download_url || t.preview_url;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t.name.replace(/\s+/g, "_")}.png`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Captions filtering
  const capCategories = ["All", ...Array.from(new Set(CAPTION_TEMPLATES.map(t => t.category)))];
  const filteredCaptions = CAPTION_TEMPLATES.filter(t => {
    if (capCat !== "All" && t.category !== capCat) return false;
    if (capQ && !t.title.toLowerCase().includes(capQ.toLowerCase()) && !t.body.toLowerCase().includes(capQ.toLowerCase())) return false;
    return true;
  });
  const copyCap = (t) => {
    navigator.clipboard?.writeText(t.body).then(() => {
      setCopiedId(t.id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  return (
    <div className="px-4 py-6 pb-28" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Link to="/" data-testid="templates-back" className="p-2 rounded-full bg-white/5 active:scale-90"><ArrowLeft className="w-4 h-4"/></Link>
        <div className="flex-1">
          <h1 className="font-outfit font-extrabold text-2xl tracking-tighter flex items-center gap-2">
            Templates <Crown className="w-5 h-5 text-[#FF7A00]"/>
          </h1>
          <p className="text-xs text-white/60">Buy professional templates with Goldly &amp; grab caption packs free</p>
        </div>
      </div>

      {/* Banner */}
      <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#FF7A00]/15 to-[#FF2A5F]/10 border border-[#FF7A00]/30 p-3 flex items-center gap-2" data-testid="templates-pro-banner">
        <Sparkles className="w-5 h-5 text-[#FF7A00] flex-shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-[#FF7A00]">Hifireels Template Store</div>
          <p className="text-[11px] text-white/70">Purchase ready-made visual templates with your Goldly wallet. Captions tab includes 30 free copy-paste templates.</p>
        </div>
      </div>

      {/* Top-level tabs */}
      <div className="flex gap-2 mb-4" data-testid="templates-toptabs">
        <button onClick={() => setTab("images")} data-testid="templates-tab-images"
          className={`flex-1 py-2.5 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 border transition ${tab === "images" ? "border-[#FF7A00] bg-[#FF7A00]/20 text-[#FF7A00]" : "border-white/10 text-white/60"}`}>
          <ImageIcon className="w-4 h-4"/>Image Templates <span className="opacity-60">({imageTemplates.length})</span>
        </button>
        <button onClick={() => setTab("captions")} data-testid="templates-tab-captions"
          className={`flex-1 py-2.5 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 border transition ${tab === "captions" ? "border-[#00E5FF] bg-[#00E5FF]/20 text-[#00E5FF]" : "border-white/10 text-white/60"}`}>
          <FileText className="w-4 h-4"/>Captions <span className="opacity-60">(free)</span>
        </button>
      </div>

      {/* ==================== IMAGE TEMPLATES STORE ==================== */}
      {tab === "images" && (
        <>
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"/>
            <input data-testid="templates-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search templates…"
              className="w-full pl-9 pr-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] text-sm"/>
          </div>

          {/* Category chips */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-3" data-testid="templates-categories">
            {IMAGE_CATEGORIES.map(c => {
              const active = cat === c;
              return (
                <button key={c} onClick={() => setCat(c)} data-testid={`templates-cat-${c.toLowerCase()}`}
                  className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition ${active ? "border-[#FF7A00] bg-[#FF7A00]/20 text-[#FF7A00]" : "border-white/10 text-white/60"}`}>
                  {CAT_LABELS[c] || c}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]"/></div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-white/50 py-8 text-sm" data-testid="templates-empty">No templates match your filter.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="templates-grid">
              {filtered.map(t => (
                <button key={t.id} onClick={() => { setPreviewing(t); setBuyMsg(""); }}
                  data-testid={`template-${t.id}`}
                  className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 active:scale-95 transition group">
                  <div className={`relative w-full ${t.aspect_ratio === "1:1" ? "aspect-square" : t.aspect_ratio === "4:5" ? "aspect-[4/5]" : t.aspect_ratio === "16:9" ? "aspect-video" : "aspect-[9/16]"}`}>
                    <img src={t.preview_url} alt={t.name} className="w-full h-full object-cover" loading="lazy"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"/>
                    {t.tier === "premium" && (
                      <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md bg-[#FF7A00]/95 text-black text-[9px] font-extrabold flex items-center gap-0.5">
                        <Crown className="w-2.5 h-2.5"/>PREMIUM
                      </div>
                    )}
                    {t.owned && (
                      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-emerald-500/95 text-black text-[9px] font-extrabold flex items-center gap-0.5">
                        <CheckCircle2 className="w-2.5 h-2.5"/>OWNED
                      </div>
                    )}
                  </div>
                  <div className="p-2 text-left">
                    <div className="text-xs font-bold truncate">{t.name}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-white/50 uppercase">{CAT_LABELS[t.category] || t.category}</span>
                      <span className="text-[11px] font-extrabold text-[#FF7A00] flex items-center gap-0.5">
                        <Coins className="w-3 h-3"/>{t.price_goldly}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Preview / Buy Modal */}
          {previewing && (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setPreviewing(null)} data-testid="template-preview-modal">
              <div className="w-full max-w-md bg-[#0a0a0f] border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-3 border-b border-white/10">
                  <div className="min-w-0">
                    <h3 className="font-bold text-base truncate">{previewing.name}</h3>
                    <p className="text-[10px] text-white/50 truncate">{previewing.description}</p>
                  </div>
                  <button onClick={() => setPreviewing(null)} data-testid="template-preview-close" className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4"/></button>
                </div>
                <div className="p-3 max-h-[60vh] overflow-auto">
                  <img src={previewing.preview_url} alt={previewing.name} className="w-full rounded-xl"/>
                </div>
                <div className="p-3 border-t border-white/10">
                  {previewing.owned ? (
                    <button onClick={() => download(previewing)} data-testid="template-download-btn"
                      className="w-full py-3 rounded-full bg-emerald-500 text-black font-extrabold flex items-center justify-center gap-2 active:scale-95">
                      <Download className="w-4 h-4"/>Download (Owned)
                    </button>
                  ) : (
                    <button onClick={() => buy(previewing)} disabled={buying} data-testid="template-buy-btn"
                      className="w-full py-3 rounded-full bg-[#FF7A00] text-black font-extrabold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95">
                      <ShoppingBag className="w-4 h-4"/>
                      {buying ? "Processing…" : <>Buy for <Coins className="w-4 h-4"/>{previewing.price_goldly} Goldly</>}
                    </button>
                  )}
                  {buyMsg && (
                    <div data-testid="template-buy-msg" className={`mt-2 text-center text-xs ${buyMsg.includes("Not enough") || buyMsg.includes("failed") ? "text-[#FF2A5F]" : "text-emerald-300"}`}>
                      {buyMsg}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ==================== CAPTION TEMPLATES (FREE) ==================== */}
      {tab === "captions" && (
        <>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"/>
            <input data-testid="captions-search" value={capQ} onChange={e => setCapQ(e.target.value)} placeholder="Search captions…"
              className="w-full pl-9 pr-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF] text-sm"/>
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-4" data-testid="captions-categories">
            {capCategories.map(c => {
              const active = capCat === c;
              return (
                <button key={c} onClick={() => setCapCat(c)} data-testid={`captions-cat-${c.toLowerCase()}`}
                  className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition ${active ? "border-[#00E5FF] bg-[#00E5FF]/20 text-[#00E5FF]" : "border-white/10 text-white/60"}`}>
                  {c}
                </button>
              );
            })}
          </div>

          {filteredCaptions.length === 0 ? (
            <div className="text-center text-white/50 py-8 text-sm" data-testid="captions-empty">No captions match.</div>
          ) : (
            <div className="space-y-3" data-testid="captions-list">
              {filteredCaptions.map(t => (
                <div key={t.id} data-testid={`caption-${t.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-[#00E5FF]/15 text-[#00E5FF]">{t.category}</span>
                      <h3 className="text-sm font-extrabold truncate">{t.title}</h3>
                    </div>
                    <button onClick={() => copyCap(t)} data-testid={`caption-copy-${t.id}`}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-1 active:scale-95 ${copiedId === t.id ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40" : "bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF]"}`}>
                      {copiedId === t.id ? <><CheckCircle2 className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap text-xs text-white/80 leading-relaxed font-sans">{t.body}</pre>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
